<template>
  <div class="patient-list">
    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-between">
      <div class="d-flex align-items-center">
        <div class="">
          <a
            class="hover-pointer"
            style="font-size: 14px"
            @click="showReportRequestsModal = true"
          >
            <i class="fa fa-download"></i>
            <span class="ml-2 text-primary" style="font-size: 14px"
              >Export</span
            >
          </a>
        </div>
      </div>
      <div class="d-flex flex-row justify-content-end align-items-center">
        <top-paginator
          :total="listTotal"
          :page="page"
          :perPage="pageSize"
          @change-page="changePage"
        />
      </div>
    </div>
    <div class="grid-container mt-10">
      <grid
        ref="grid"
        :groupFeature="false"
        :autoHeight="true"
        :columns="columns"
        :data="items"
        :store="gridConfig.store"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :searchFeature="true"
        @storesort="gridListeners.storeSort"
        @paint="gridListeners.paint"
        :stripeFeature="true"
      ></grid>
    </div>
    <report-request-index
      v-if="showReportRequestsModal"
      v-model="showReportRequestsModal"
      :reportFilters="exportFilter"
      reportName="AuditRecords"
    />
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import TopPaginator from "../controls/TopPaginator.vue";
import { formatDate } from "../../helpers/date";
import ReportRequestIndex from "../report_request/ReportRequestIndex.vue";

export default {
  name: "AuditHistoryListView",
  components: { Grid, TopPaginator, ReportRequestIndex },
  props: [
    "showing",
    "list",
    "listTotal",
    "exportFilter",
    "orderBy",
    "orderByDescending",
    "page",
    "pageSize"
  ],
  data() {
    const self = this;
    return {
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: false,
          checkbox: false,
          rowCheckboxSelection: false,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          sorters: [
            {
              field: this.orderBy,
              ascending: !this.orderByDescending
            }
          ],
          remoteSort: true
        }
      },
      gridListeners: {
        storeSort: sortData => {
          self.showDetailsRows = {};
          this.store = sortData.source;
          if (sortData.sorters.length === 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.$emit("change-order", {
              orderBy: field,
              orderByDescending: !ascending
            });
          }
        },
        paint: paintEvent => {
          if (paintEvent.firstPaint) {
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "button.show-details",
              click: async event => {
                event.stopPropagation();
                const auditLogIndex = event.srcElement.dataset.index;
                self.showDetailsRows = {
                  ...self.showDetailsRows,
                  [auditLogIndex]: true
                };
              },
              capture: true,
              thisObj: this
            });
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "button.hide-details",
              click: async event => {
                event.stopPropagation();
                const auditLogIndex = event.srcElement.dataset.index;
                self.showDetailsRows = {
                  ...self.showDetailsRows,
                  [auditLogIndex]: false
                };
              },
              capture: true,
              thisObj: this
            });
          }
        }
      },
      lastTimeout: null,
      firstShowing: true,
      store: null,
      showDetailsRows: {},
      showReportRequestsModal: false
    };
  },
  mounted() {},
  methods: {
    changePage(page) {
      self.showDetailsRows = {};
      this.$emit("change-page", {
        ...this.pagination,
        page
      });
    }
  },
  computed: {
    items() {
      const self = this;
      return this.list.map(item => {
        const singleLineHeight = 45;
        const charsDetailsPerLine = 70;
        const maxFactor = Math.ceil(
          (item.details ? item.details.length : 0) / charsDetailsPerLine
        );

        let detailsHeight = 0;
        if (maxFactor > 1) detailsHeight += singleLineHeight * (maxFactor - 1);
        else detailsHeight = 30;

        let eventDetailsHeight =
          maxFactor > 1 ? detailsHeight + 30 : singleLineHeight + detailsHeight;

        const rowHeight =
          self.firstShowing && self.showDetailsRows[item.id]
            ? eventDetailsHeight
            : singleLineHeight;
        return {
          ...item,
          rowHeight: rowHeight
        };
      });
    },
    columns() {
      const self = this;
      return [
        {
          text: "Date & Time",
          field: "datetime",
          type: "template",
          width: 180,
          editor: false,
          template: ({ record }) => {
            return formatDate(record.data.locationDateTime);
          }
        },
        {
          text: "User",
          field: "user",
          type: "template",
          width: 240,
          editor: false,
          template: ({ record }) => {
            return record.data.userName;
          }
        },
        {
          text: "Location",
          field: "location",
          type: "template",
          width: 240,
          editor: false,
          template: ({ record }) => {
            return record.data.locationName;
          }
        },
        {
          text: "IP Address",
          field: "ipAddress",
          type: "template",
          width: 100,
          editor: false,
          template: ({ record }) => {
            return record.data.ipAddress;
          }
        },
        {
          text: "Event",
          field: "event",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            const detailsLength = record.details ? record.details.length : 0;
            const charsDetailsPerLine = 70;
            let numberOfLines = Math.ceil(detailsLength / charsDetailsPerLine);
            if (!numberOfLines) numberOfLines = 1;
            const detailsHeight = 16.8 * numberOfLines + 13.2;
            const details = self.showDetailsRows[record.data.id]
              ? `<div class="d-flex flex-column w-75 flex-wrap justify-content-center" style="white-space: initial; min-height: 30px; height: ${detailsHeight}px"><div><b>Details: </b>${record
                  .data.details ?? ""}</div></div>`
              : "";

            const btnStyles = `margin-left: auto; padding: 3px 15px 3px 6px; background: #d3d3d39e; color: #323232c9; border-color: #d3d3d39e; font-weight: 700; font-size: 0.80em; width: 110px;`;
            const btn = self.showDetailsRows[record.data.id]
              ? `<button class="btn btn-secondary btn-sm hide-details" style="${btnStyles}" data-index="${record.data.id}"><i class="fas fa-chevron-up" style="margin-right: 10px"></i>Hide Details</button>`
              : `<button class="btn btn-secondary btn-sm show-details" style="${btnStyles}" data-index="${record.data.id}"><i class="fas fa-chevron-down" style="margin-right: 10px"></i>Show Details</button>`;

            return `
            <div class="w-100 d-flex flex-row flex-wrap">
              <div class="d-flex flex-column w-75 justify-content-center">${record.data.event}</div> 
              <div class="d-flex flex-column w-25">${btn}</div> 
              ${details}
            </div>
            `;
          },
          sortable: false
        }
      ];
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}
.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  min-width: 250px;
}
.client-card {
  min-width: 180px;
  max-width: 180px;
  height: 315px;
}
.little-circle {
  width: 10px;
  height: 10px;
}
.little-circle-exclamation {
  width: 20px;
  height: 20px;
}
.little-circle-exclamation-container {
  width: 15px;
  height: 15px;
  margin-right: 5px;
}
.client-avatar {
  width: 100px;
  height: 100px;
  text-align: center;
}
.client-data {
  font-size: 0.8rem;
  word-break: break-all;
  overflow: hidden;
  text-overflow: clip;
}
.client-card:hover {
  cursor: pointer;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.5);
  transform: translateY(-3px);
  transition-duration: 0.3s;
  transition-property: transform;
}
.clients-list {
  height: calc(100vh - 264px - var(--filter-height));
  overflow-y: auto;
}
.border-yellow {
  border-color: #ffc107 !important;
}
.bg-yellow {
  background-color: #ffc107 !important;
}
.client-text-data {
  height: 180px;
}
.client-eoc-data {
  height: 119px;
}
.filter-values {
  width: 250px;
}
.filter-value {
  position: relative;
  min-width: 100px;
  color: #a6a6a6;
  font-size: 14px;
}
.filter-value-delete {
  position: absolute;
  top: -8px;
  right: -8px;
}
.filter-value-delete:hover {
  cursor: pointer;
}
.clear-all-filters:hover {
  cursor: pointer;
}
.filter-values {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
}
.filters-container {
  overflow-x: auto;
}
.separator-or {
  margin-bottom: -13px;
}
div.disabled {
  pointer-events: none;

  /* for "disabled" effect */
  opacity: 0.5;
  background: #ccc;
}
.max-2-lines {
  max-height: 38px;
}
.tooltip-icon-bg {
  top: 5px;
  left: 5px;
  width: 10px;
  height: 13px;
  z-index: 1;
}
.patient-list >>> .b-grid-container .b-container {
  max-height: calc(100vh - 290px - var(--filter-height));
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-badge {
  color: white;
  font-size: 11px;
  width: 40px;
  height: 16px;
  text-align: center;
  border-radius: 25px;
  line-height: 1.5;
}
.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}
.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #0084ff !important;
  border-bottom-width: 2px;
}
.patient-list .grid-container .b-grid-container {
  display: flex;
  width: 100%;
  height: 100% !important;
  flex: 1;
}
.patient-list .grid-container .b-grid {
  max-height: 100%;
  width: 100%;
}
.mt-10 {
  margin-top: 10px;
}
</style>
